import { useState } from 'react'
import { useForge } from '../data/useForge'
import { useI18n } from '../i18n/useI18n'
import { Icon } from './Icon'

export function ForgeInstaller() {
  const { t } = useI18n()
  const {
    selectedSkills,
    selectedWorkflowObject,
    platform,
    tool,
    setPlatform,
    setTool,
    command,
    selectedSkillObjects,
    clearSelection,
  } = useForge()
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(command)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // ignore
    }
  }

  const hasSelection = selectedSkills.length > 0 || selectedWorkflowObject

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-outline-variant p-5">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-container text-on-primary-container">
            <Icon name="construction" size={22} filled />
          </div>
          <div>
            <h2 className="font-headline-md text-headline-md text-on-surface">{t.installer.title}</h2>
            <p className="font-body-md text-body-md text-on-surface-variant">{t.installer.subtitle}</p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-5">
        {/* Tabs */}
        <div className="mb-6 flex flex-col gap-1">
          <button className="flex items-center gap-3 rounded-xl bg-primary-container p-3 font-label-sm text-label-sm font-bold text-on-primary-container ring-1 ring-primary">
            <Icon name="construction" size={18} filled />
            {t.installer.commandBuilder}
          </button>
          <button className="flex items-center gap-3 rounded-xl p-3 font-label-sm text-label-sm text-on-surface-variant transition-colors hover:bg-surface-container-high">
            <Icon name="list_alt" size={18} />
            {t.installer.selectedItems}
            {selectedSkills.length > 0 && (
              <span className="ml-auto flex h-6 w-6 items-center justify-center rounded-full border border-outline-variant bg-surface-container text-xs font-semibold text-on-surface">
                {selectedSkills.length}
              </span>
            )}
          </button>
          <button className="flex items-center gap-3 rounded-xl p-3 font-label-sm text-label-sm text-on-surface-variant transition-colors hover:bg-surface-container-high">
            <Icon name="terminal" size={18} />
            {t.installer.platformSelector}
          </button>
        </div>

        <div className="mb-6 h-px bg-outline-variant" />

        {/* Configuration */}
        <div className="mb-6 space-y-5">
          <div>
            <label className="mb-2 block font-label-sm text-label-sm text-on-surface">
              {t.installer.targetPlatform}
            </label>
            <div className="flex rounded-lg border border-outline-variant bg-surface-container p-1">
              <button
                type="button"
                onClick={() => setPlatform('maclinux')}
                className={`flex-1 rounded-md px-3 py-1.5 font-label-sm text-label-sm transition-colors ${
                  platform === 'maclinux'
                    ? 'bg-surface-container-lowest text-on-surface shadow-sm'
                    : 'text-on-surface-variant hover:text-on-surface'
                }`}
              >
                {t.installer.macLinux}
              </button>
              <button
                type="button"
                onClick={() => setPlatform('windows')}
                className={`flex-1 rounded-md px-3 py-1.5 font-label-sm text-label-sm transition-colors ${
                  platform === 'windows'
                    ? 'bg-surface-container-lowest text-on-surface shadow-sm'
                    : 'text-on-surface-variant hover:text-on-surface'
                }`}
              >
                {t.installer.windowsWsl}
              </button>
            </div>
          </div>

          <div>
            <label className="mb-2 block font-label-sm text-label-sm text-on-surface">
              {t.installer.targetTool}
            </label>
            <div className="flex rounded-lg border border-outline-variant bg-surface-container p-1">
              {(['claude', 'codex', 'cursor'] as const).map((tOption) => (
                <button
                  key={tOption}
                  type="button"
                  onClick={() => setTool(tOption)}
                  className={`flex-1 rounded-md px-2 py-1.5 font-label-sm text-label-sm transition-colors ${
                    tool === tOption
                      ? 'bg-surface-container-lowest text-on-surface shadow-sm'
                      : 'text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  {tOption.charAt(0).toUpperCase() + tOption.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Selected workflow */}
        {selectedWorkflowObject && (
          <div className="mb-5 rounded-lg border border-outline-variant bg-surface-container-lowest p-4">
            <div className="mb-2 flex items-center justify-between">
              <span className="font-label-sm text-label-sm font-semibold text-on-surface">{t.installer.selectedWorkflow}</span>
              <button
                type="button"
                onClick={() => clearSelection()}
                className="font-label-sm text-label-sm text-on-surface-variant hover:text-primary"
              >
                Clear
              </button>
            </div>
            <div className="flex items-center gap-2 font-body-md text-body-md text-on-surface">
              <Icon name={selectedWorkflowObject.icon} size={18} className="text-primary" />
              {selectedWorkflowObject.name}
            </div>
          </div>
        )}

        {/* Selected skills */}
        {selectedSkillObjects.length > 0 && (
          <div className="mb-5">
            <div className="mb-2 flex items-center justify-between">
              <span className="font-label-sm text-label-sm font-semibold text-on-surface">{t.installer.selectedSkills}</span>
              {!selectedWorkflowObject && (
                <button
                  type="button"
                  onClick={() => clearSelection()}
                  className="font-label-sm text-label-sm text-on-surface-variant hover:text-primary"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedSkillObjects.map((skill) => (
                <span
                  key={skill.id}
                  className="inline-flex items-center gap-1 rounded-full border border-outline-variant bg-surface-container px-2 py-1 font-label-sm text-label-sm text-on-surface"
                >
                  <Icon name={skill.icon} size={14} />
                  {skill.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!hasSelection && (
          <div className="mb-6 rounded-xl border border-dashed border-outline-variant p-4 text-center">
            <Icon name="widgets" size={24} className="mx-auto mb-2 text-outline" />
            <p className="font-body-md text-body-md text-on-surface-variant">{t.installer.emptyState}</p>
          </div>
        )}

        {/* Generated command */}
        <div className="mt-auto">
          <h3 className="mb-2 font-headline-md text-headline-md text-on-surface">{t.installer.generatedCommand}</h3>
          <div className="relative rounded-lg border border-secondary bg-inverse-surface p-4 code-active-border">
            <button
              type="button"
              onClick={handleCopy}
              className="absolute right-2 top-2 text-on-surface-variant transition-colors hover:text-primary"
              title="Copy"
            >
              <Icon name="content_copy" size={16} />
            </button>
            <pre className="overflow-x-auto whitespace-pre-wrap break-all font-code-md text-code-md text-surface-container-high">
              {command}
            </pre>
          </div>
          <p className="mt-3 font-label-sm text-label-sm text-on-surface-variant">{t.installer.mockDisclaimer}</p>
        </div>
      </div>

      <div className="border-t border-outline-variant p-5">
        <button
          type="button"
          onClick={handleCopy}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-3 font-label-sm text-label-sm font-bold text-on-primary shadow-[inset_0_1px_0_rgba(255,255,255,0.2)] transition-opacity hover:opacity-90"
        >
          <Icon name={copied ? 'check' : 'play_arrow'} size={18} />
          {copied ? t.installer.copied : t.installer.copyRun}
        </button>
        <div className="mt-4 flex justify-center gap-4">
          <a
            href="#"
            className="flex items-center gap-1 font-label-sm text-label-sm text-on-surface-variant transition-colors hover:text-primary"
          >
            <Icon name="description" size={14} />
            {t.installer.documentation}
          </a>
          <a
            href="https://github.com/HughYau/AcademicForge"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 font-label-sm text-label-sm text-on-surface-variant transition-colors hover:text-primary"
          >
            <Icon name="code" size={14} />
            {t.installer.github}
          </a>
        </div>
      </div>
    </div>
  )
}
